import React from 'react';
import numeral from 'numeral';
import _ from 'lodash';
import { compose } from 'recompose';
import axios from 'axios';
import { Icon, Segment, Divider, Popup, Table, Message } from 'semantic-ui-react';
import { YoutubeIcon, FacebookIcon, InstagramIcon } from '../../components/dyzio-icons';
import { authService } from '../../utils/auth-service';
import TargetLink from '../../components/target-link';
import InstagramAuthModal from '../../components/instagram-auth-modal';
import AuthorisationButton from '../../components/authorisation-button';

import hasRestClient from '../../hocs/has-rest-client';
import hasJSONAPIClient from '../../hocs/has-jsonapi-client';
import ThemeSelector from '../../themes/theme-selector';

const THEME = ThemeSelector.theme();

const cf = num => (num ? numeral(num).format('0,0') : '-');

const FACEBOOK_OPTIONS = {
  clientId: '307083449705342',
};

const getFacebookOptions = () => {
  const clientId = THEME.facebookClientId;
  return clientId
    ? { clientId }
    : FACEBOOK_OPTIONS;
};

const facebookCallbackUrl = 'https://uiqcrs8dhb.execute-api.eu-west-1.amazonaws.com/production/callback/facebook';

const AuthorisationStatus = ({ platform, accessTokens }) => {
  const isAuthorised = accessTokens && accessTokens[platform];
  if (isAuthorised) {
    return <Popup trigger={<Icon name="check" color="green" size="big" />} content="channel authorised!" />;
  }
  return <Popup trigger={<Icon name="x" color="grey" size="big" />} content="not authorised" />;
};

const getHandle = (influencer, platform) => {
  const handle = influencer[platform];
  if (!handle) return '-';
  return platform === 'youtube' ? influencer.youtubeChannelTitle : influencer[platform];
};

const isTokenMatch = ({ token, username, platform }) => {
  const usernameRegExp = new RegExp(username, 'i');
  const safePlatform = _.toUpper(platform);
  return token.username.match(usernameRegExp) && token.platform === safePlatform;
};

const PlatformIcons = {
  youtube: <YoutubeIcon size="big" />,
  instagram: <InstagramIcon size="big" />,
  facebook: <FacebookIcon size="big" />,
};

const AuthRow = ({ influencer, platform, handleAuthClick, handleRemoveClick, tokens }) => {
  if (!influencer[platform]) return null; // do not render a row if there is no handle for the platform

  const username = influencer[platform];

  const { accessTokens } = influencer;
  const tokenExists = accessTokens && accessTokens[platform];
  let isAuthorised = false;

  if (_.find(tokens, token => isTokenMatch({ token, username, platform }))) {
    console.log('found the token');
    isAuthorised = true;
  }

  return (
    <Table.Row key={platform}>
      <Table.Cell>
        { PlatformIcons[platform] }
      </Table.Cell>
      <Table.Cell>
        {getHandle(influencer, platform)}
      </Table.Cell>
      <Table.Cell>
        {cf(influencer[`${platform}Count`])}&nbsp;&nbsp;
        {
          platform === 'instagram' &&
          <Popup
            trigger={<Icon name="briefcase" color={influencer.instagramBusinessAccount ? 'pink' : 'grey'} size="large" />}
            content={`${influencer.instagramBusinessAccount ? '' : 'NOT an '}Instagram Business Account`}
          />
        }
      </Table.Cell>
      <Table.Cell>

        <AuthorisationButton
          platform={platform}
          disabled={!_.isString(influencer[platform])}
          accessTokens={influencer.accessTokens}
          handleAuthClick={handleAuthClick}
          handleRemoveClick={() => handleRemoveClick(influencer.id, platform)}
        />
      </Table.Cell>


      <Table.Cell>
        <AuthorisationStatus platform={platform} accessTokens={influencer.accessTokens} />
      </Table.Cell>
      <Table.Cell>
        {
          tokenExists && isAuthorised &&
          <Popup
            trigger={<Icon name="smile" size="big" color="green" />}
            content={`Authorisation succeeded for ${getHandle(influencer, platform)} on ${_.toUpper(platform)}.`}
          />
        }
        {
          tokenExists && !isAuthorised &&
          <Popup
            trigger={<Icon name="frown" size="big" color="orange" />}
            content={`Authorisation failed for ${getHandle(influencer, platform)} on ${_.toUpper(platform)}.`}
          />
        }
      </Table.Cell>
    </Table.Row>
  );
};


class InfluencerAuthorisation extends React.Component {
  constructor() {
    super();
    this.state = {
      loading: true,
      instagramAuthModalVisible: false,
    };
  }

  componentDidMount() {
    const { app_metadata: { dyzio: { o_id: organisationId } } } = authService.getProfile();
    this.loadInfluencer(organisationId);
  }

  loadTokens = influencerId => {
    // todo create proxyServerClient
    const tokenUrl = `https://1nfoz04muh.execute-api.eu-west-1.amazonaws.com/production/tokens/${influencerId}`;
    return axios.get(tokenUrl)
      .then(response => {
        const { tokens } = response.data.data;
        console.log(tokens);
        return tokens;
      })
      .catch(e => {
        console.log(e);
      });
  }

  loadInfluencer = organisationId => {
    const { jsonAPIClient } = this.props;
    jsonAPIClient.get('organisations', organisationId, { include: ['influencer'] })
      .then(organisation => organisation.toJSONTree())
      .then(org => {
        this.loadTokens(org.influencer.id)
          .then(tokens => {
            this.setState({
              influencer: org.influencer,
              loading: false,
              tokens,
            });
          });
      })
      .catch(e => {
        console.log(e);
      });
  }

  authoriseInstagram = () => {
    const instagramScopes = [
      'manage_pages',
      'instagram_manage_insights',
      'instagram_basic',
    ];
    this.authorise('instagram', instagramScopes);
  }

  removeAuthorisation = (influencerId, platform) => {
    const dto = {
      data: {
        influencerId,
        platform,
      },
    };

    axios.post('https://uiqcrs8dhb.execute-api.eu-west-1.amazonaws.com/production/auth/remove', dto)
      .then(response => {
        const updatedInfluencer = response.data.data;
        updatedInfluencer.id = influencerId;
        console.log(updatedInfluencer);
        this.setState({
          influencer: updatedInfluencer,
        });
      })
      .catch(err => console.log(err));
  }

  authorise = (platform, scopeList) => {
    const isInstagram = platform === 'instagram';
    const { influencer } = this.state;
    const { hostname, origin } = window.location;
    const windowLocation = window.location.href;
    const influencerId = influencer.id;
    const callbackURL = isInstagram ? `${origin}/token/generate` : windowLocation;
    const scopes = _.join(scopeList, ',');
    const version = 2;

    const state = encodeURIComponent(
      JSON.stringify({
        influencerId,
        callbackURL,
        platform,
        hostname,
        version,
      }),
    );

    const facebookOptions = getFacebookOptions();
    console.log(`Facebook Opts => ${JSON.stringify(facebookOptions, null, 2)}`);
    const opts = [
      'https://www.facebook.com/v2.11/dialog/oauth',
      `?client_id=${facebookOptions.clientId}`,
      '&response_type=code',
      `&redirect_uri=${facebookCallbackUrl}`,
      `&scope=${scopes}`,
      '&auth_type=rerequest',
      `&state=${state}`,
    ];
    window.location.href = _.join(opts, '');
  }

  authoriseFacebook = () => {
    const facebookScopes = [
      'manage_pages',
      'read_insights',
    ];
    this.authorise('facebook', facebookScopes);
  };

  authoriseYoutube = () => {
    const { influencer } = this.state;
    const windowLocation = window.location.href;

    axios.post('https://uiqcrs8dhb.execute-api.eu-west-1.amazonaws.com/production/oauth/youtube/url', {
      data: {
        influencerId: influencer.id,
        callbackURL: windowLocation,
      },
    }).then(r => {
      window.location.href = r.data.headers.Location;
    });
  }

  triggerInstagramAuthorisationModal = () => this.setState({
    instagramAuthModalVisible: !this.state.instagramAuthModalVisible,
  })

  render() {
    const { loading, influencer, tokens, instagramAuthModalVisible } = this.state;

    const authChannels = [
      {
        platform: 'youtube',
        onAuthClick: this.authoriseYoutube,
      },
      {
        platform: 'instagram',
        onAuthClick: this.triggerInstagramAuthorisationModal,
      },
      {
        platform: 'facebook',
        onAuthClick: this.authoriseFacebook,
      },
    ];

    let isAuthorised = false;

    console.log(tokens);

    const isVoxogram = window.location.hostname === 'app.voxogram.com';
    const platformName = isVoxogram ? 'voxogram' : 'dyzio';

    if (influencer) {
      const { accessTokens } = influencer;
      _.forEach(authChannels, chan => {
        const username = influencer[chan.platform];
        const findParams = { username, platform: _.toUpper(chan.platform) };
        console.log(findParams);
        if (_.find(tokens, findParams)) {
          console.log('found the token');
          isAuthorised = true;
        }
        // if (accessTokens && accessTokens[chan.platform])
      });
    }


    return (
      <div className="page">
        <div style={{ height: 140, backgroundColor: '#FFF', borderBottom: '1px solid #EEE' }}>
          <div className="page-hero">
            <h1 style={{ paddingTop: 40 }}><Icon name="lock" color="orange" />Authorisation</h1>
            <h4 className="light">Authorise your social media channels</h4>
            {
              !isVoxogram &&
              <p style={{ textAlign: 'right ' }}>
                for help click here
                &nbsp;
                <TargetLink
                  renderIf
                  href="https://help.dyzio.co/influencer-authentication/authorising-your-social-channels-to-dyzio"
                >
                  Influencer Authorisation<Icon name="help" />
                </TargetLink>
              </p>
            }
          </div>
          <div className="page-content">
            <Segment loading={loading} basic="very" style={{ padding: 0 }}>
              {
                influencer &&
                <Segment>
                  <p>Use the buttons below to grant and remove authorisation to your social channels. You can remove the access by clicking the button again.</p>
                  <p>When your channel is authorised the <Icon name="x" color="grey" /> will turn to a <Icon name="check" color="green" />.</p>
                  <Divider />
                  <Message
                    info
                    icon="shield"
                    header={`${platformName} only ever requests READ ACCESS to your social channels. We CANNOT edit, post or delete anything.`}
                    content={<p>This allows us to request insights data (e.g. reach, impressions) from the platform and share it with your client.</p>}
                  />
                  <Divider />

                  <Message
                    warning
                    icon="instagram"
                    header="Instagram requirements."
                    content={
                      <ul>
                        <li>Please make sure your Facebook account is set up properly (it must be confirmed).</li>
                        <li>Please also make sure your Instagram account is linked to your main Facebook page.</li>
                        <li>You must have either an Instagram&nbsp;
                          <TargetLink
                            renderIf
                            href="https://business.instagram.com/getting-started"
                          >Business Account
                          </TargetLink>

                           &nbsp;or a&nbsp;
                          <TargetLink
                            renderIf
                            href="https://help.instagram.com/2358103564437429"
                          >
                           Creator Account
                          </TargetLink>.
                        </li>
                      </ul>
                    }
                  />

                  <Divider />

                  <Table celled textAlign="center" fixed>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell width={2}>Platform</Table.HeaderCell>
                        <Table.HeaderCell width={3}>Handle</Table.HeaderCell>
                        <Table.HeaderCell width={3}>Followers</Table.HeaderCell>
                        <Table.HeaderCell width={4}>Authorise</Table.HeaderCell>
                        <Table.HeaderCell width={2}>Token Saved</Table.HeaderCell>
                        <Table.HeaderCell width={2}>Token Status</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {
                        _.map(authChannels, chan => (
                          <AuthRow
                            influencer={influencer}
                            platform={chan.platform}
                            handleAuthClick={chan.onAuthClick}
                            handleRemoveClick={this.removeAuthorisation}
                            tokens={tokens}
                          />
                        ))
                      }
                    </Table.Body>
                  </Table>

                  { isAuthorised &&
                  <div>
                    <Divider />
                    <Message
                      positive
                      icon="smile outline"
                      header="Thanks for authorising your channel!"
                      content={`${platformName} will process your token now and check that it works. We may need to contact you if there has been a problem. Please DO NOT revoke access until the campaign has finished.`}
                    />
                  </div>
                  }
                </Segment>
              }
            </Segment>
          </div>
        </div>
        {
          instagramAuthModalVisible &&
          <InstagramAuthModal
            onClose={this.triggerInstagramAuthorisationModal}
            onContinueButtonClick={this.authoriseInstagram}
          />
        }

      </div>
    );
  }
}

export default compose(
  hasRestClient,
  hasJSONAPIClient,
)(InfluencerAuthorisation);



/** WEBPACK FOOTER **
 ** ./src/containers/influencer-authorisation/index.js
 **/